import React, { useState } from "react";
import {
  Stack,
  Typography,
  Switch,
  Button,
  Grid,
  Box,
  Alert,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";

import { Surface, SectionTitle, StatusChip, ConfirmDialog } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

function ControlRow({ title, description, checked, onChange, disabled }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ py: 1.5 }}
    >
      <Box>
        <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: T.ink }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: T.muted, mt: 0.2 }}>
          {description}
        </Typography>
      </Box>
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </Stack>
  );
}

export default function StatusTab({ pharmacyId, pharmacy, onChanged, notify }) {
  const [pending, setPending] = useState(null);

  const deleted = Boolean(pharmacy?.isDeleted);

  const dialogs = {
    verification: {
      title: pending?.next ? "Approve this pharmacy?" : "Reject this pharmacy?",
      description: pending?.next
        ? "It becomes visible to customers and can receive orders."
        : "It stops receiving orders and is hidden from customers.",
      requireReason: !pending?.next,
      run: async (reason) => {
        await api.setVerification(pharmacyId, pending.next, reason);
        notify?.(pending.next ? "Pharmacy approved." : "Pharmacy rejected.");
      },
    },
    takingOrders: {
      title: pending?.next ? "Resume order-taking?" : "Pause order-taking?",
      description: pending?.next
        ? "New orders will be routed here again."
        : "It stays verified, but new orders will go elsewhere.",
      requireReason: !pending?.next,
      run: async (reason) => {
        await api.setTakingOrders(pharmacyId, pending.next, reason);
        notify?.(pending.next ? "Now taking orders." : "Order-taking paused.");
      },
    },
    delete: {
      title: "Delete this pharmacy?",
      description:
        "The login phone number is cleared so the account cannot be signed into again. Orders, invoices and GST records are kept — they have statutory retention periods. This can be undone.",
      requireReason: true,
      destructive: true,
      confirmLabel: "Delete pharmacy",
      run: async (reason) => {
        await api.softDeletePharmacy(pharmacyId, reason);
        notify?.("Pharmacy deleted.");
      },
    },
    restore: {
      title: "Restore this pharmacy?",
      description:
        "The original login phone number is returned. Approval and order-taking stay off — turn them back on deliberately.",
      requireReason: true,
      confirmLabel: "Restore",
      run: async (reason) => {
        await api.restorePharmacy(pharmacyId, reason);
        notify?.("Pharmacy restored.");
      },
    },
  };

  const active = pending ? dialogs[pending.kind] : null;

  return (
    <Stack spacing={2}>
      {deleted && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          This pharmacy is deleted. Its login is disabled and it cannot receive
          orders. Restore it to make changes.
        </Alert>
      )}

      <Surface sx={{ p: { xs: 2, md: 3 } }}>
        <SectionTitle>Current state</SectionTitle>
        <Grid container spacing={1.2}>
          <Grid item>
            <StatusChip
              status={pharmacy?.verifiedPharamacy ? "verified" : "pending"}
              label={pharmacy?.verifiedPharamacy ? "Verified" : "Not verified"}
            />
          </Grid>
          <Grid item>
            <StatusChip
              status={pharmacy?.isTakingOrders ? "accepted" : "pending"}
              label={pharmacy?.isTakingOrders ? "Taking orders" : "Paused"}
            />
          </Grid>
          <Grid item>
            <StatusChip
              status={pharmacy?.isLoggedIn ? "active" : "pending"}
              label={pharmacy?.isLoggedIn ? "Logged in" : "Not logged in"}
            />
          </Grid>
          <Grid item>
            <StatusChip
              status={pharmacy?.bankDetails?.accountNumber ? "verified" : "pending"}
              label={pharmacy?.bankDetails?.accountNumber ? "Bank on file" : "No bank"}
            />
          </Grid>
          {deleted && (
            <Grid item>
              <StatusChip status="cancelled" label="Deleted" />
            </Grid>
          )}
        </Grid>

        {pharmacy?.syncLastAt && (
          <Typography sx={{ fontSize: "0.75rem", color: T.subtle, mt: 1.5 }}>
            Inventory last synced{" "}
            {dayjs(pharmacy.syncLastAt).format("DD MMM YYYY, hh:mm A")}
            {pharmacy.syncPosKind ? ` · ${pharmacy.syncPosKind}` : ""}
          </Typography>
        )}
      </Surface>

      <Surface sx={{ p: { xs: 2, md: 3 } }}>
        <SectionTitle>Controls</SectionTitle>

        <ControlRow
          title="Approved"
          description="Whether this pharmacy is verified and visible to customers."
          checked={Boolean(pharmacy?.verifiedPharamacy)}
          disabled={deleted}
          onChange={(e) => setPending({ kind: "verification", next: e.target.checked })}
        />
        <Divider />
        <ControlRow
          title="Taking orders"
          description="Whether new orders are routed to this pharmacy right now."
          checked={Boolean(pharmacy?.isTakingOrders)}
          disabled={deleted}
          onChange={(e) => setPending({ kind: "takingOrders", next: e.target.checked })}
        />
      </Surface>

      <Surface
        sx={{
          p: { xs: 2, md: 3 },
          borderColor: deleted ? T.border : tint(T.error, 0.4),
        }}
      >
        <SectionTitle>{deleted ? "Restore" : "Danger zone"}</SectionTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Typography sx={{ fontSize: "0.85rem", color: T.muted, maxWidth: 560 }}>
            {deleted
              ? "Return the original login phone number and reopen the account. Approval and order-taking stay off."
              : "Deleting clears the login phone number so the account cannot be signed into. Order and invoice history is kept for statutory retention, and the deletion can be undone."}
          </Typography>
          <Button
            variant={deleted ? "contained" : "outlined"}
            onClick={() => setPending({ kind: deleted ? "restore" : "delete" })}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              whiteSpace: "nowrap",
              ...(deleted
                ? { bgcolor: T.primary, "&:hover": { bgcolor: "#013838" } }
                : { color: T.error, borderColor: tint(T.error, 0.5) }),
            }}
          >
            {deleted ? "Restore pharmacy" : "Delete pharmacy"}
          </Button>
        </Stack>
      </Surface>

      <ConfirmDialog
        open={Boolean(pending)}
        title={active?.title}
        description={active?.description}
        confirmLabel={active?.confirmLabel || (pending?.next ? "Enable" : "Disable")}
        destructive={active?.destructive || (pending && pending.next === false)}
        requireReason={active?.requireReason}
        onConfirm={async (reason) => {
          await active.run(reason);
          onChanged?.();
        }}
        onClose={() => setPending(null)}
      />
    </Stack>
  );
}
